/**
 * Built-in profile avatars (`preset:{id}`). Keep ids in sync with backend AVATAR_PRESETS.
 * Lucide + Tabler (MIT) for helmet / steering wheel / tire.
 */
import {
  Bird,
  Bot,
  Car,
  Fish,
  Ghost,
  Medal,
  Rocket,
  Squirrel,
  Trophy,
} from 'lucide-react';

function IconBase({ children, className, strokeWidth = 2, ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  );
}

/** Tabler Icons MIT — closed-face racing helmet */
const RacingHelmet = (p) => (
  <IconBase {...p}>
    <path d="M12 4a9 9 0 0 1 5.656 16h-11.312a9 9 0 0 1 5.656-16" />
    <path d="M20 9h-8.8a1 1 0 0 0-.968 1.246c.507 2 1.596 3.418 3.268 4.254 2 1 4.333 1.5 7 1.5" />
  </IconBase>
);

/** Tabler Icons MIT — steering wheel */
const SteeringWheel = (p) => (
  <IconBase {...p}>
    <path d="M3 12a9 9 0 1 0 18 0 9 9 0 1 0-18 0" />
    <path d="M10 12a2 2 0 1 0 4 0 2 2 0 1 0-4 0" />
    <path d="M12 14v7" />
    <path d="M10 12 3.25 10" />
    <path d="M14 12 20.75 10" />
  </IconBase>
);

/** Tire: outer rubber + rim + hub + tread ticks (Tabler wheel + rubber band). */
const Tire = (p) => (
  <IconBase {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="6.25" />
    <circle cx="12" cy="12" r="2.25" />
    <path d="M12 3v1.75M12 19.25V21M3 12h1.75M19.25 12H21" />
    <path d="M5.6 5.6 6.9 6.9M17.1 17.1 18.4 18.4M18.4 5.6 17.1 6.9M6.9 17.1 5.6 18.4" />
  </IconBase>
);

export const AVATAR_PRESETS = [
  // Animals (Lucide)
  { id: 'bird', Icon: Bird },
  { id: 'squirrel', Icon: Squirrel },
  { id: 'fish', Icon: Fish },
  { id: 'ghost', Icon: Ghost },
  // Race
  { id: 'helmet', Icon: RacingHelmet },
  { id: 'racecar', Icon: Car },
  { id: 'steering-wheel', Icon: SteeringWheel },
  { id: 'tire', Icon: Tire },
  // Fun (Lucide)
  { id: 'bot', Icon: Bot },
  { id: 'rocket', Icon: Rocket },
  { id: 'trophy', Icon: Trophy },
  { id: 'medal', Icon: Medal },
];

/** Grid columns for avatar pickers (signup + profile). */
export const AVATAR_PRESET_COLS = 4;

/** Shared glyph size so Lucide + Tabler read the same in the picker. */
export const AVATAR_ICON_CLASS = 'size-[22px] shrink-0';

export const parseAvatarPreset = (url) =>
  typeof url === 'string' && url.startsWith('preset:') ? url.slice(7) : null;

export const avatarPresetIcon = (url) => {
  const id = parseAvatarPreset(url);
  return id ? AVATAR_PRESETS.find((p) => p.id === id)?.Icon : null;
};
